import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CourseBadges from "../components/CourseBadges";
import { Award, BadgeCheck, ExternalLink, Linkedin, ArrowRight, FileDown } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { SuccessCard } from "../components/SuccessCard";

const fmt = (v) => (v ? new Date(v).toLocaleDateString("el-GR", { day: "2-digit", month: "long", year: "numeric" }) : "");

export default function Certificates() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/academy/certificates").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const linkedinUrl = (cert) => {
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: cert.course_title || cert.course?.title || "",
      organizationName: "SportScience Academy",
      issueYear: String(new Date(cert.issued_at || Date.now()).getFullYear()),
      issueMonth: String(new Date(cert.issued_at || Date.now()).getMonth() + 1),
      certId: cert.credential_id || cert.code || "",
    });
    if (cert.verification_url) params.set("certUrl", cert.verification_url);
    return `https://www.linkedin.com/profile/add?${params.toString()}`;
  };

  return (
    <>
      <div className="max-w-5xl mx-auto px-5 sm:px-6 pt-14 pb-20" data-testid="certificates-page">
        <span className="badge-lime">{t("certs.badge")}</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl mt-5 text-[#0B1B3A]" data-testid="certificates-title">
          {t("certs.title")}
        </h1>
        <p className="text-base text-[#475569] mt-4 max-w-2xl">{t("certs.subtitle")}</p>

        {rows === null ? (
          <p className="text-[#64748B] mt-10">{t("common.loading")}</p>
        ) : rows.length === 0 ? (
          <div className="card-dark p-12 mt-10 text-center" data-testid="certificates-empty">
            <Award className="w-10 h-10 text-[#1D4ED8] mx-auto" />
            <p className="text-[#64748B] mt-5">{t("certs.empty")}</p>
            <Link to="/academy" className="btn-blue mt-7 inline-flex">
              {t("certs.browse")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10" data-testid="certificates-list">
            {rows.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="card-dark overflow-hidden flex flex-col relative"
                data-testid={`certificate-card-${c.code}`}
              >
                {c.course?.badge_image && <img src={c.course.badge_image} alt="" className="w-full h-36 object-cover" />}
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/badges/course/${c.course_id}.png`} alt="" className="w-24 h-24 object-contain absolute top-2 right-2 drop-shadow-lg" data-testid={`certificate-course-badge-${c.code}`} />
                <div className="p-7 flex-1 flex flex-col">
                  <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1D4ED8]">
                    <BadgeCheck className="w-3.5 h-3.5" />
                    {c.kind === "path" ? t("certs.diploma") : t("certs.internal")}
                  </span>
                  <h2 className="font-heading font-black text-lg text-[#0B1B3A] mt-3">{(lang === "en" && c.course_title_en) || c.course_title || c.course?.title}</h2>
                  <p className="text-sm text-[#475569] mt-2">{t("certs.holder")}: {c.holder}</p>
                  <p className="text-xs text-[#64748B] mt-1">
                    {fmt(c.issued_at)} · {t("certs.score")}: {c.score}% · <span className="font-mono" data-testid={`certificate-serial-${c.code}`}>{c.serial || c.code}</span>
                  </p>

                  <div className="flex flex-wrap gap-2 mt-6">
                    {c.issued_at && (
                      <a
                        data-testid={`certificate-pdf-${c.code}`}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${c.id}/pdf?lang=${lang}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-blue !px-5 !py-2.5 text-xs"
                      >
                        <FileDown className="w-4 h-4" /> {t("certs.downloadPdf")}
                      </a>
                    )}
                    {c.issued_at && (
                    <a
                      data-testid={`certificate-linkedin-${c.code}`}
                      href={linkedinUrl(c)}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-outline-light !px-5 !py-2.5 text-xs"
                    >
                      <Linkedin className="w-4 h-4" /> {t("certs.addLinkedin")}
                    </a>
                    )}
                    {c.verification_url ? (
                      <a data-testid={`certificate-verify-${c.code}`} href={c.verification_url} target="_blank" rel="noreferrer" className="btn-outline-light !px-5 !py-2.5 text-xs">
                        <ExternalLink className="w-4 h-4" /> {t("certs.verify")}
                      </a>
                    ) : (
                      <Link data-testid={`certificate-verify-${c.code}`} to={`/verify/${c.serial || c.code}`} className="btn-outline-light !px-5 !py-2.5 text-xs">
                        <BadgeCheck className="w-4 h-4" /> {t("certs.verify")}
                      </Link>
                    )}
                    {c.issued_at && <SuccessCard cert={c} />}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
